import React, { useEffect, useState, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  TextField,
  TablePagination,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import useStyles from './styles';
import axios from '../../../../utils/axios';
import { getProductsPerPageByCompanyId } from '../../../../requests/api/product';
import Snackbar from '../../../../components/Snackbar';

const AssociatedProductsTables = ({ associatedProducts, setAssociatedProducts, isEditing }) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const [companyId, setCompanyId] = useState(axios?.defaults?.params?.companyId);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [availableFilter, setAvailableFilter] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('');
  const [availablePage, setAvailablePage] = useState(0);
  const [selectedPage, setSelectedPage] = useState(0);
  const [availableTotal, setAvailableTotal] = useState(0);
  const [message, setMessage] = useState('');
  const rowsPerPage = 10;

  // Company ID setup
  useEffect(() => {
    if (companyId) return;
    const interval = setInterval(() => {
      const currentId = axios?.defaults?.params?.companyId;
      if (currentId !== undefined) {
        setCompanyId(currentId);
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [companyId]);

  const fetchAvailableProducts = useCallback(async () => {
    if (!companyId) return;
    const idsInTheGroup = associatedProducts.map((p) => p.id).join(',');
    const response = await getProductsPerPageByCompanyId(
      availablePage,
      rowsPerPage,
      availableFilter,
      '',
      idsInTheGroup,
    );
    if (response.success) {
      const transformedProducts = response.data.data.products
        .filter((product) => !associatedProducts.some((ap) => ap.id === product.id))
        .map((product) => ({
          ...product,
          name: product.productName,
          internalCode: product.internal_code,
        }));
      setAvailableProducts(transformedProducts);
      setAvailableTotal(response.data.data.totalProducts);
    } else {
      setMessage(t('common.failedLoad'));
    }
  }, [companyId, availablePage, availableFilter, associatedProducts, t]);

  useEffect(() => {
    if (isEditing) {
      fetchAvailableProducts();
    }
  }, [fetchAvailableProducts, isEditing]);

  const selectedProducts = associatedProducts.filter((product) => (
    product.name.toLowerCase().includes(selectedFilter.toLowerCase())
  ));

  const handleAvailableFilterChange = (value) => {
    setAvailableFilter(value);
    setAvailablePage(0);
  };

  const handleAvailablePageChange = (newPage) => {
    setAvailablePage(newPage);
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    if (source.droppableId === 'available' && destination.droppableId === 'selected') {
      const productId = parseInt(result.draggableId, 10);
      const product = availableProducts.find((p) => p.id === productId);
      if (product) {
        setAssociatedProducts([...associatedProducts, product]);
      }
    } else if (source.droppableId === 'selected' && destination.droppableId === 'available') {
      const productId = parseInt(result.draggableId, 10);
      setAssociatedProducts(associatedProducts.filter((product) => product.id !== productId));
    }
  };

  const renderTable = (title, products, droppableId, filter, setFilter, page, setPage) => {
    const paginatedProducts = droppableId === 'available'
      ? products
      : products.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
      <Paper className={styles.groupTable}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <strong>{title}</strong>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={3}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder={t('productGroup.filterByName')}
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('productGroup.name')}</TableCell>
                <TableCell>{t('productGroup.description')}</TableCell>
                <TableCell>{t('productGroup.code')}</TableCell>
              </TableRow>
            </TableHead>
            <Droppable droppableId={droppableId}>
              {(provided) => (
                <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                  {paginatedProducts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        {t('productGroup.noProducts')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedProducts.map((product) => (
                      <Draggable
                        key={product.id}
                        draggableId={product.id.toString()}
                        index={products.indexOf(product)}
                      >
                        {(provided) => (
                          <TableRow
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <TableCell>{product.name}</TableCell>
                            <TableCell>{product.description}</TableCell>
                            <TableCell>{product.internalCode}</TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    ))
                  )}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={droppableId === 'available' ? availableTotal : selectedProducts.length}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5]}
          labelRowsPerPage={t('common.rowsPerPage')}
          labelDisplayedRows={({ count }) => {
            const start = page * rowsPerPage + 1;
            const end = Math.min((page + 1) * rowsPerPage, count);
            return t('common.showingRecords', { from: start, to: end, total: count });
          }}
        />
      </Paper>
    );
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Grid container spacing={2}>
        {isEditing && (
          <Grid item xs={6}>
            {renderTable(t('productGroup.availableProducts'), availableProducts, 'available', availableFilter, handleAvailableFilterChange, availablePage, handleAvailablePageChange)}
          </Grid>
        )}
        <Grid item xs={isEditing ? 6 : 12}>
          {renderTable(t('productGroup.associatedProducts'), selectedProducts, 'selected', selectedFilter, setSelectedFilter, selectedPage, setSelectedPage)}
        </Grid>
      </Grid>
      <Snackbar
        open={message !== ''}
        message={message}
        severity="error"
        onClose={() => setMessage('')}
      />
    </DragDropContext>
  );
};

export default AssociatedProductsTables;
